import { getClientDisplayName } from "@/lib/utils/client-utils";
import { ClientEntityType } from "@/lib/supabase/supabaseTypes";

/**
 * Sorting utilities for Debitoren accounting views
 * Provides type-specific comparators and a generic sorting helper
 */

type SortDirection = 'asc' | 'desc';

/**
 * Compares two date strings, handling null/undefined values
 * Null/undefined dates are sorted to the end
 */
export const compareDates = (
	aValue: string | null | undefined,
	bValue: string | null | undefined,
	direction: SortDirection = 'asc'
): number => {
	// Handle null/undefined - always sort to end regardless of direction
	if (!aValue && !bValue) return 0;
	if (!aValue) return 1;
	if (!bValue) return -1;

	try {
		const aDate = new Date(aValue);
		const bDate = new Date(bValue);

		// Check for invalid dates
		if (isNaN(aDate.getTime()) && isNaN(bDate.getTime())) return 0;
		if (isNaN(aDate.getTime())) return 1;
		if (isNaN(bDate.getTime())) return -1;

		const comparison = aDate.getTime() - bDate.getTime();
		return direction === 'desc' ? -comparison : comparison;
	} catch {
		// Fallback to string comparison if date parsing fails
		return compareStrings(aValue, bValue, direction);
	}
};

/**
 * Compares two monetary amounts, handling null/undefined values
 * Null/undefined amounts are sorted to the end
 */
export const compareMoney = (
	aValue: number | null | undefined,
	bValue: number | null | undefined,
	direction: SortDirection = 'asc'
): number => {
	// Handle null/undefined - always sort to end regardless of direction
	if (aValue === null || aValue === undefined) {
		if (bValue === null || bValue === undefined) return 0;
		return 1;
	}
	if (bValue === null || bValue === undefined) return -1;

	const comparison = aValue - bValue;
	return direction === 'desc' ? -comparison : comparison;
};

/**
 * Compares two percentage values, handling null/undefined
 * Null/undefined percentages are sorted to the end
 */
export const comparePercentages = (
	aValue: number | null | undefined,
	bValue: number | null | undefined,
	direction: SortDirection = 'asc'
): number => {
	// Same as money comparison - it's just a number
	return compareMoney(aValue, bValue, direction);
};

/**
 * Compares two recipient names, handling company vs. personal names
 * For personal names: sorts by first name, then last name
 * For companies: sorts by company name
 * Companies and persons are mixed together with equal priority
 * Handles null/undefined by sorting to end
 */
export const compareRecipientNames = (
	aRow: any,
	bRow: any,
	direction: SortDirection = 'asc'
): number => {
	const aIsCompany = aRow.recipient_is_company;
	const bIsCompany = bRow.recipient_is_company;

	// For companies, use company name as primary sort key
	// For persons, use first name as primary sort key
	const aPrimarySortKey = aIsCompany
		? (aRow.recipient_company_name || '').toLowerCase()
		: (aRow.recipient_last_name || '').toLowerCase();

	const bPrimarySortKey = bIsCompany
		? (bRow.recipient_company_name || '').toLowerCase()
		: (bRow.recipient_last_name || '').toLowerCase();

	// Primary sort: company name vs first name (with equal priority)
	let comparison = aPrimarySortKey.localeCompare(bPrimarySortKey, 'de-DE');

	// If primary keys are equal and both are persons, use last name as secondary sort
	if (comparison === 0 && !aIsCompany && !bIsCompany) {
		const aFirstName = (aRow.recipient_first_name || '').toLowerCase();
		const bFirstName = (bRow.recipient_first_name || '').toLowerCase();
		comparison = aFirstName.localeCompare(bFirstName, 'de-DE');
	}

	return direction === 'desc' ? -comparison : comparison;
};

/**
 * Compares two client names, handling company vs. personal names
 * For personal names: sorts by first name, then last name
 * For companies: sorts by company name
 * Companies and persons are mixed together with equal priority
 * Handles null/undefined by sorting to end
 */
export const compareClientNames = (
	aRow: any,
	bRow: any,
	direction: SortDirection = 'asc'
): number => {
	const aIsCompany = aRow.client_is_company;
	const bIsCompany = bRow.client_is_company;

	// For companies, use company name as primary sort key
	// For persons, use first name as primary sort key
	const aPrimarySortKey = aIsCompany
		? (aRow.client_company_name || '').toLowerCase()
		: (aRow.client_last_name || '').toLowerCase();

	const bPrimarySortKey = bIsCompany
		? (bRow.client_company_name || '').toLowerCase()
		: (bRow.client_last_name || '').toLowerCase();

	// Primary sort: company name vs first name (with equal priority)
	let comparison = aPrimarySortKey.localeCompare(bPrimarySortKey, 'de-DE');

	// If primary keys are equal and both are persons, use last name as secondary sort
	if (comparison === 0 && !aIsCompany && !bIsCompany) {
		const aFirstName = (aRow.client_first_name || '').toLowerCase();
		const bFirstName = (bRow.client_first_name || '').toLowerCase();
		comparison = aFirstName.localeCompare(bFirstName, 'de-DE');
	}

	return direction === 'desc' ? -comparison : comparison;
};

/**
 * Compares two invoice numbers
 * Handles alphanumeric invoice numbers properly (e.g., "RG-2024-001")
 */
export const compareInvoiceNumbers = (
	aValue: string | null | undefined,
	bValue: string | null | undefined,
	direction: SortDirection = 'asc'
): number => {
	// Handle null/undefined
	if (!aValue && !bValue) return 0;
	if (!aValue) return 1;
	if (!bValue) return -1;

	// Use locale-aware string comparison for invoice numbers
	// This handles alphanumeric strings better than simple string comparison
	const comparison = aValue.localeCompare(bValue, 'de-DE', {
		numeric: true,
		sensitivity: 'base'
	});

	return direction === 'desc' ? -comparison : comparison;
};

/**
 * Generic string comparison with locale awareness
 * Handles null/undefined by sorting to end
 */
export const compareStrings = (
	aValue: string | null | undefined,
	bValue: string | null | undefined,
	direction: SortDirection = 'asc'
): number => {
	// Handle null/undefined
	if (!aValue && !bValue) return 0;
	if (!aValue) return 1;
	if (!bValue) return -1;

	const comparison = aValue.toLowerCase().localeCompare(
		bValue.toLowerCase(),
		'de-DE'
	);

	return direction === 'desc' ? -comparison : comparison;
};

/**
 * Generic number comparison
 * Handles null/undefined by sorting to end
 */
export const compareNumbers = (
	aValue: number | null | undefined,
	bValue: number | null | undefined,
	direction: SortDirection = 'asc'
): number => {
	return compareMoney(aValue, bValue, direction);
};

/**
 * Compares payment status based on payment percentage
 * Orders: Unpaid (0%) < Partial (0-100%) < Fully Paid (100%)
 */
export const comparePaymentStatus = (
	aRow: any,
	bRow: any,
	direction: SortDirection = 'asc'
): number => {
	// Calculate payment percentages
	const aInvoiceValue = aRow.invoice_value || 0;
	const bInvoiceValue = bRow.invoice_value || 0;
	const aTotalAllocated = aRow.total_allocated_amount || 0;
	const bTotalAllocated = bRow.total_allocated_amount || 0;

	const aPercentage = aInvoiceValue > 0 ? (aTotalAllocated / aInvoiceValue) * 100 : 0;
	const bPercentage = bInvoiceValue > 0 ? (bTotalAllocated / bInvoiceValue) * 100 : 0;

	return comparePercentages(aPercentage, bPercentage, direction);
};

/**
 * Maps column IDs to their appropriate comparison function
 */
const columnComparators: Record<string, (a: any, b: any, direction: SortDirection) => number> = {
	// Dates
	invoice_date: (a, b, dir) => compareDates(a.invoice_date, b.invoice_date, dir),
	invoice_canceled_at: (a, b, dir) => compareDates(a.invoice_canceled_at, b.invoice_canceled_at, dir),
	valuta_date: (a, b, dir) => compareDates(a.valuta_date, b.valuta_date, dir),

	// Money/Currency
	invoice_value: (a, b, dir) => compareMoney(a.invoice_value, b.invoice_value, dir),
	total_allocated_amount: (a, b, dir) => compareMoney(a.total_allocated_amount, b.total_allocated_amount, dir),
	vat_amount: (a, b, dir) => compareMoney(a.vat_amount, b.vat_amount, dir),
	net_amount: (a, b, dir) => {
		const aNet = (a.invoice_value || 0) - (a.vat_amount || 0);
		const bNet = (b.invoice_value || 0) - (b.vat_amount || 0);
		return compareMoney(aNet, bNet, dir);
	},
	open_amount: (a, b, dir) => {
		const aOpen = Math.max(0, (a.invoice_value || 0) - (a.total_allocated_amount || 0));
		const bOpen = Math.max(0, (b.invoice_value || 0) - (b.total_allocated_amount || 0));
		return compareMoney(aOpen, bOpen, dir);
	},
	gross_amount: (a, b, dir) => compareMoney(a.invoice_value, b.invoice_value, dir),

	// Percentages
	payment_percentage: comparePaymentStatus,
	effective_tax_rate: (a, b, dir) => comparePercentages(a.effective_tax_rate, b.effective_tax_rate, dir),
	tax_rate: (a, b, dir) => comparePercentages(a.effective_tax_rate, b.effective_tax_rate, dir),

	// Names
	recipient_name: compareRecipientNames,
	client_last_name: compareClientNames,

	// Invoice numbers
	invoice_doc_number: (a, b, dir) => compareInvoiceNumbers(a.invoice_doc_number, b.invoice_doc_number, dir),

	// Strings
	reason_for_cancelation: (a, b, dir) => compareStrings(a.reason_for_cancelation, b.reason_for_cancelation, dir),
};

/**
 * Generic sorting helper for table data
 * Automatically selects the appropriate comparator based on column ID
 * Applies secondary sorting by invoice_doc_number when primary values are equal
 *
 * @param data - Array of data to sort
 * @param sortBy - Column ID to sort by
 * @param sortDirection - Sort direction ('asc' or 'desc')
 * @param secondarySortBy - Optional secondary sort column (default: 'invoice_doc_number')
 * @returns Sorted array (does not mutate original)
 */
export const sortTableData = <T extends Record<string, any>>(
	data: T[],
	sortBy: string,
	sortDirection: SortDirection = 'asc',
	secondarySortBy: string = 'invoice_doc_number'
): T[] => {
	// Create a copy to avoid mutating the original array
	const dataCopy = [...data];

	// Get the comparator for the primary sort column
	const primaryComparator = columnComparators[sortBy];

	// Get the comparator for the secondary sort column
	const secondaryComparator = columnComparators[secondarySortBy];

	dataCopy.sort((a, b) => {
		// Primary sort
		let comparison = 0;

		if (primaryComparator) {
			comparison = primaryComparator(a, b, sortDirection);
		} else {
			// Fallback to generic comparison if no specific comparator is defined
			const aValue = a[sortBy];
			const bValue = b[sortBy];

			if (typeof aValue === 'number' && typeof bValue === 'number') {
				comparison = compareNumbers(aValue, bValue, sortDirection);
			} else {
				comparison = compareStrings(
					aValue?.toString(),
					bValue?.toString(),
					sortDirection
				);
			}
		}

		// Secondary sort if primary values are equal
		if (comparison === 0 && sortBy !== secondarySortBy && secondaryComparator) {
			// Always sort invoice numbers descending as secondary sort
			comparison = secondaryComparator(a, b, 'desc');
		}

		return comparison;
	});

	return dataCopy;
};
